import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Play, Sparkles, Volume2, Maximize, Pause } from 'lucide-react';
import {
  Eyebrow, RaceBib, Tom, MarginNote, EvidenceCard,
} from '@/components/foundry';
import { submitCurrentLabWork } from '@/services/kickstartBlueprint';
import { useAppAuth } from '@/lib/appAuth';

interface LessonContent {
  num: string;
  title: string;
  duration: string;
  state: 'done' | 'current' | 'next' | 'locked';
  hook: string;
  chapters: Array<{ time: string; label: string }>;
  takeaway: string;
  labWorkPrompt: string;
  labWorkPlaceholder: string;
  /** Used by the "you finished this lesson" state — Tom's sign-off. */
  signoff?: { from: 'tom'; body: string };
}

const LESSONS: Record<string, LessonContent> = {
  '01': {
    num: '01',
    title: 'The Pocket Genius problem',
    duration: '6 min',
    state: 'done',
    hook:
      'Your members already have a smarter library than yours. It\'s in their pocket. Naming what that means changes everything you teach next.',
    chapters: [
      { time: '0:00', label: 'The shift you can\'t opt out of' },
      { time: '1:42', label: 'What "Pocket Genius" means in practice' },
      { time: '3:18', label: 'The 47-lesson trap' },
      { time: '4:55', label: 'The two questions to answer first' },
    ],
    takeaway: 'Static content gets weaker as the model gets stronger. Method gets stronger.',
    labWorkPrompt: 'In one sentence: where in your business is static content already weaker than ChatGPT?',
    labWorkPlaceholder: 'My membership library — by lesson 2 most members give up and ask GPT instead…',
    signoff: { from: 'tom', body: '✓ Logged. You spotted the right thing. Lesson 02 picks up where this leaves off.' },
  },
  '02': {
    num: '02',
    title: 'The one shift that flips AI from threat to advantage',
    duration: '8 min',
    state: 'current',
    hook:
      'Your method, steering the model. Not theory — a concrete framework you can run today.',
    chapters: [
      { time: '0:00', label: 'Recap: the Pocket Genius problem' },
      { time: '1:08', label: 'What a Steering Layer is' },
      { time: '3:22', label: 'The 4 pillars: sequence, evidence, POV, standards' },
      { time: '5:40', label: 'How PJPT made theirs explicit' },
      { time: '7:12', label: 'Your move for next lesson' },
    ],
    takeaway: 'Generic AI is a Pocket Genius. Your method, made explicit, is what survives.',
    labWorkPrompt: 'Name one element of YOUR method that AI can\'t reproduce on its own. Be specific.',
    labWorkPlaceholder: 'My PJPT clinic onboarding sequence — the order of evidence I ask for from week 1 to week 8…',
  },
  '03': {
    num: '03',
    title: 'The no-sales-call funnel',
    duration: '11 min',
    state: 'next',
    hook: 'How PJPT replaced 11h/wk of sales calls with a personalised AI bridge that converts cold traffic.',
    chapters: [
      { time: '0:00', label: 'Why sales calls are the bottleneck' },
      { time: '2:14', label: 'The personalised bridge in 90 seconds' },
      { time: '4:55', label: 'PJPT\'s actual bridge headline' },
      { time: '7:30', label: 'What the warm bridge does that a sales call can\'t' },
      { time: '9:12', label: 'Welcome path · 3-email sequence' },
    ],
    takeaway: 'A personalised AI bridge converts as well as a sales call, and runs while you sleep.',
    labWorkPrompt: 'Write a one-sentence version of YOUR bridge headline. Don\'t overthink it — first draft is fine.',
    labWorkPlaceholder: 'For clinic owners stuck below £20k/mo who already tried Facebook ads…',
  },
  '04': {
    num: '04',
    title: 'Your Steering Layer',
    duration: '7 min',
    state: 'locked',
    hook: 'The part of your business AI can\'t reproduce — and why making it explicit is the fastest growth move.',
    chapters: [],
    takeaway: '',
    labWorkPrompt: '',
    labWorkPlaceholder: '',
  },
  '05': {
    num: '05',
    title: 'What changes in 30 days',
    duration: '9 min',
    state: 'locked',
    hook: 'The first working version of your AI-native system, what it looks like, and the path to ship it.',
    chapters: [],
    takeaway: '',
    labWorkPrompt: '',
    labWorkPlaceholder: '',
  },
};

const ORDER = ['01', '02', '03', '04', '05'];

interface Props {
  num: string;
  onBack: () => void;
  onOpenLesson: (num: string) => void;
  onOpenBreakthrough: () => void;
}

export default function LessonPage({ num, onBack, onOpenLesson, onOpenBreakthrough }: Props) {
  const lesson = LESSONS[num] ?? LESSONS['02'];
  const idx = ORDER.indexOf(lesson.num);
  const prevNum = idx > 0 ? ORDER[idx - 1] : null;
  const nextNum = idx < ORDER.length - 1 ? ORDER[idx + 1] : null;
  const isLast = !nextNum;

  const { getToken } = useAppAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [labWork, setLabWork] = useState('');
  const [submitted, setSubmitted] = useState(lesson.state === 'done');
  const [submittingState, setSubmittingState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset local form state when navigating between lessons. Without this, the in-flight
  // textarea content from lesson N leaks into lesson N+1 on next-lesson navigation.
  useEffect(() => {
    setLabWork('');
    setSubmitted(lesson.state === 'done');
    setSubmittingState('idle');
    setSubmitError(null);
  }, [lesson.num, lesson.state]);

  const isLocked = lesson.state === 'locked';

  async function handleSubmit() {
    const trimmed = labWork.trim();
    if (!trimmed || isLocked) return;
    setSubmittingState('saving');
    setSubmitError(null);
    try {
      const token = (await getToken?.({ skipCache: true })) ?? null;
      await submitCurrentLabWork(lesson.num, { text: trimmed }, token);
      setSubmitted(true);
      setSubmittingState('idle');
    } catch (err) {
      // Keep the textarea content intact so the Builder can retry. The blueprint compute
      // gate on the Breakthrough page will surface "Finish lesson N first" if this fails.
      setSubmittingState('error');
      setSubmitError(err instanceof Error ? err.message : 'Could not save your lab work.');
    }
  }

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-7 sm:px-6 lg:px-8">
      {/* Top breadcrumb + lesson nav */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:text-fd-text"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Kickstart
        </button>
        <div className="flex items-center gap-2 font-geist-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
          <span>LESSON <b className="text-fd-text">{lesson.num}</b> OF 05</span>
          <span className="hidden sm:inline">·</span>
          <span className="hidden sm:inline">{lesson.duration}</span>
        </div>
      </div>

      {/* Lesson header */}
      <header className="mb-5">
        <Eyebrow className="mb-2">{`LESSON ${lesson.num} · KICKSTART · ${lesson.duration.toUpperCase()}`}</Eyebrow>
        <h1 className="font-geist text-[28px] font-medium tracking-[-0.025em] text-fd-text [text-wrap:balance] sm:text-[34px] lg:text-[40px]">
          {lesson.title}
        </h1>
        <p className="mt-3 max-w-[760px] text-[15px] leading-[1.55] text-fd-text-muted lg:text-[16px]">
          {lesson.hook}
        </p>
      </header>

      {/* Two-column: video + Lab Work (col-8) / sidebar (col-4) */}
      <div className="grid grid-cols-12 gap-5">
        {/* Video player + Lab Work */}
        <div className="col-span-12 grid gap-5 lg:col-span-8">
          {/* Video player */}
          <div
            className="relative overflow-hidden rounded-[14px] border-[1.5px] border-fd-text bg-fd-text"
            style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
          >
            <div className="relative aspect-video w-full">
              {/* Placeholder thumbnail — would be video poster in production */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'radial-gradient(900px 360px at 30% 20%, oklch(0.30 0.005 95), oklch(0.16 0.005 95) 60%)',
                }}
              />
              {/* Big lesson number watermark */}
              <span className="absolute left-6 top-4 font-geist-mono text-[64px] font-semibold leading-none tracking-[-0.03em] text-fd-accent/20">
                {lesson.num}
              </span>

              {/* Play/Pause overlay */}
              <button
                onClick={() => !isLocked && setIsPlaying((v) => !v)}
                disabled={isLocked}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                className="absolute inset-0 grid place-items-center transition-opacity hover:bg-fd-text/10 disabled:cursor-not-allowed"
              >
                <span
                  className="grid h-20 w-20 place-items-center rounded-full border-[1.5px] border-fd-text bg-fd-accent text-fd-text transition-transform hover:scale-105"
                  style={{ boxShadow: '4px 4px 0 0 var(--fd-text)' }}
                >
                  {isPlaying ? <Pause className="h-7 w-7 fill-current" /> : <Play className="h-7 w-7 fill-current" />}
                </span>
              </button>

              {/* Custom controls bar */}
              <div className="absolute bottom-0 left-0 right-0 flex items-center gap-3 bg-gradient-to-t from-fd-text/80 to-transparent px-4 py-3">
                <span className="font-geist-mono text-[11px] font-bold text-fd-accent">
                  {isPlaying ? '0:42' : '0:00'} / {lesson.duration.replace(' min', ':00')}
                </span>
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/15">
                  <div className="h-full bg-fd-accent" style={{ width: isPlaying ? '8%' : '0%' }} />
                </div>
                <button aria-label="Volume" className="text-fd-accent/80 hover:text-fd-accent">
                  <Volume2 className="h-4 w-4" />
                </button>
                <button aria-label="Fullscreen" className="text-fd-accent/80 hover:text-fd-accent">
                  <Maximize className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Lab Work form */}
          <div
            className="relative rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-6 sm:p-7"
            style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
          >
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <Eyebrow className="mb-1.5">LAB WORK · 2 MINUTES</Eyebrow>
                <h3 className="font-geist text-[18px] font-semibold tracking-[-0.01em]">
                  {lesson.labWorkPrompt}
                </h3>
              </div>
              {submitted && (
                <RaceBib n="" label="Submitted" tone="teal" size="sm" shadow="none" />
              )}
            </div>

            {!submitted ? (
              <>
                <textarea
                  value={labWork}
                  onChange={(e) => setLabWork(e.target.value)}
                  placeholder={lesson.labWorkPlaceholder}
                  rows={4}
                  disabled={isLocked}
                  className="w-full resize-none rounded-[8px] border-[1.5px] border-fd-border-strong/50 bg-fd-bg p-3.5 font-geist text-[14px] leading-[1.55] text-fd-text outline-none transition-shadow placeholder:text-fd-text-subtle focus:border-fd-accent-line focus:shadow-[0_0_0_4px_var(--fd-accent-soft)] disabled:cursor-not-allowed disabled:opacity-60"
                />
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <p className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
                    {submittingState === 'saving' ? 'Saving…' : `${labWork.length} chars`}
                  </p>
                  <button
                    disabled={!labWork.trim() || isLocked || submittingState === 'saving'}
                    onClick={handleSubmit}
                    className="inline-flex h-10 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text transition-all hover:-translate-x-px hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
                    style={{ boxShadow: '3px 3px 0 0 var(--fd-text)' }}
                  >
                    {submittingState === 'saving' ? 'Saving…' : 'Submit Lab Work'}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                {submittingState === 'error' && submitError && (
                  <p className="mt-3 rounded-[6px] border border-fd-coral/40 bg-fd-coral-soft px-3 py-2 font-geist text-[12.5px] leading-[1.4] text-fd-text">
                    Couldn't save: {submitError}. Try once more — your draft is still here.
                  </p>
                )}
              </>
            ) : (
              <EvidenceCard kind="locked" eyebrow={`SUBMITTED · ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}>
                {lesson.signoff?.body ?? 'Saved. Tom will see this in your Game Plan context if you continue to the full Lab.'}
              </EvidenceCard>
            )}
          </div>

          {/* Lesson navigation row */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-fd-border bg-fd-paper p-3 sm:p-4">
            <button
              disabled={!prevNum}
              onClick={() => prevNum && onOpenLesson(prevNum)}
              className="inline-flex h-9 items-center gap-2 rounded-[4px] px-3 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {prevNum ? `Lesson ${prevNum} · ${LESSONS[prevNum].title}` : 'No previous lesson'}
            </button>
            {isLast ? (
              <button
                onClick={onOpenBreakthrough}
                disabled={!submitted}
                className="inline-flex h-10 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-text px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-accent transition-all hover:-translate-x-px hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
                style={{ boxShadow: '3px 3px 0 0 var(--fd-accent)' }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Open The Breakthrough
              </button>
            ) : nextNum ? (
              <button
                onClick={() => onOpenLesson(nextNum)}
                className="inline-flex h-10 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text transition-all hover:-translate-x-px hover:-translate-y-px"
                style={{ boxShadow: '3px 3px 0 0 var(--fd-text)' }}
              >
                Lesson {nextNum} · {LESSONS[nextNum].title}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Sidebar: chapters + takeaway + Tom moment */}
        <aside className="col-span-12 grid gap-5 lg:col-span-4">
          {/* Chapters */}
          <div className="rounded-[14px] border border-fd-border bg-fd-paper p-5">
            <Eyebrow className="mb-3 block">CHAPTERS</Eyebrow>
            <div className="grid gap-1">
              {lesson.chapters.length === 0 && (
                <p className="text-[12.5px] text-fd-text-muted">Chapters appear once the lesson unlocks.</p>
              )}
              {lesson.chapters.map((c, i) => (
                <button
                  key={c.time}
                  className="flex items-baseline gap-3 rounded-[6px] px-2 py-1.5 text-left transition-colors hover:bg-fd-surface-1"
                >
                  <span className="font-geist-mono text-[11px] font-semibold text-fd-text-subtle">
                    {c.time}
                  </span>
                  <span className={['flex-1 font-geist text-[13px]', i === 0 ? 'font-medium text-fd-text' : 'text-fd-text-muted'].join(' ')}>
                    {c.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Key takeaway */}
          {lesson.takeaway && (
            <div className="relative rounded-[14px] border-[1.5px] border-fd-text bg-fd-accent p-5" style={{ boxShadow: '4px 4px 0 0 var(--fd-text)' }}>
              <Eyebrow className="mb-1.5 block">THE TAKEAWAY</Eyebrow>
              <p className="font-geist text-[15px] font-medium leading-[1.45] text-fd-text">
                {lesson.takeaway}
              </p>
              <MarginNote tilt={-3} tone="ink" size="sm" className="absolute -top-3 right-3 bg-fd-paper px-1.5 py-0.5 rounded-[3px] border border-fd-text">
                pin this.
              </MarginNote>
            </div>
          )}

          {/* Tom moment */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative grid grid-cols-[auto_1fr] items-center gap-3 rounded-[14px] border border-dashed border-fd-border-strong/40 bg-fd-paper p-4"
          >
            <Tom variant="walking-on" className="h-[100px] w-auto shrink-0" />
            <div>
              <Eyebrow className="mb-1.5 block">TOM · YOUR AI CO-FOUNDER</Eyebrow>
              <p className="text-[13px] leading-[1.5] text-fd-text">
                I'll see your Lab Work the moment you submit it. If you go on to the full Lab, your Game
                Plan starts pre-loaded with everything from Kickstart.
              </p>
            </div>
          </motion.div>
        </aside>
      </div>
    </div>
  );
}
